import { FloatingFormItem } from '@/components/_common';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import { FormatType } from '@/core/domain/reports/movements/dailyMovement/dailyMovement.types';
import { useGenerateInventoryRegisterBook } from '@/core/domain/reports/movements/purchase/ReportPurchase.hooks';
import {
  ReportInventoryRegisterBookForm,
  ReportInventoryRegisterBookParms,
} from '@/core/domain/reports/movements/purchase/ReportPurchase.types';
import { useReportGenerate, useWindowSize } from '@/hooks';
import useFileLoading, { FileType } from '@/hooks/useFileLoading';
import { ReportCallback } from '@/hooks/useReportGenerate';
import {
  Alert,
  Button,
  Checkbox,
  Col,
  Form,
  Input,
  Modal,
  ModalProps,
  Row,
} from 'antd';

interface Props extends ModalProps {
  inventoryCurrentCountId: number;
  onCancel: () => void;
}

const InventoryReportP7Modal = ({
  inventoryCurrentCountId,
  ...props
}: Props) => {
  const [form] = Form.useForm<ReportInventoryRegisterBookForm>();

  const { isMobile } = useWindowSize();
  const report = useReportGenerate();
  const generateInventoryRegisterBook = useGenerateInventoryRegisterBook();

  const { setFileLoading, setFileUnloading, fileLoading } = useFileLoading();

  const handleGenerateReport = async (
    format: FileType,
    callback?: ReportCallback,
  ) => {
    setFileLoading(format);
    try {
      const formValues = await form.validateFields();

      const obj: ReportInventoryRegisterBookParms = {
        ...formValues,
        inventoryCurrentCountId,
        format: format === 'CSV' ? FormatType.CSV : FormatType.PDF,
      };

      const result = await generateInventoryRegisterBook.mutateAsync(obj);

      if (callback) {
        callback(result);
      }
    } catch {}
    setFileUnloading(format);
  };

  const handleView = async () => {
    await handleGenerateReport('REPORT_VIEW', (result) =>
      report.openFile({
        result,
        fileName: `Relatório_P7_${inventoryCurrentCountId}.pdf`,
        fileType: 'application/pdf',
      }),
    );
    props.onCancel();
  };

  const handlePdf = async () => {
    await handleGenerateReport(FormatType.PDF, (result) =>
      report.downloadFile({
        result,
        fileName: `Relatório_P7_${inventoryCurrentCountId}.pdf`,
      }),
    );
    props.onCancel();
  };

  const handleCsv = async () => {
    await handleGenerateReport(FormatType.CSV, (result) =>
      report.downloadFile({
        result,
        fileName: `Relatório_P7_${inventoryCurrentCountId}.csv`,
      }),
    );
    props.onCancel();
  };

  const onCancel = () => {
    form.resetFields();
    props.onCancel();
  };

  return (
    <Modal
      {...props}
      title="Gerar relatório P7"
      width="50%"
      destroyOnClose
      footer={
        <SpaceForButton
          flexDirection={!isMobile ? 'row-reverse' : 'column'}
          justifyContent="flex-end"
        >
          <Button block onClick={onCancel}>
            Cancelar
          </Button>

          <Button
            block
            type="primary"
            onClick={handleView}
            key="viewReport"
            loading={fileLoading.REPORT_VIEW}
          >
            Visualizar
          </Button>

          <Button
            block
            type="primary"
            onClick={handlePdf}
            key="CSV"
            loading={fileLoading.PDF}
          >
            Gerar PDF
          </Button>

          <Button
            block
            type="primary"
            onClick={handleCsv}
            key="CSV"
            loading={fileLoading.CSV}
          >
            Gerar CSV
          </Button>
        </SpaceForButton>
      }
    >
      <Form form={form}>
        <Row gutter={[16, 16]}>
          <Alert
            type="info"
            description=" O relatório P7 é um documento contábil que registra o inventário de uma
            empresa, listando os produtos, matérias-primas e materiais de embalagem.
            Ele é também conhecido como Livro de Registro de Inventário."
          />

          <Col xs={24}>
            <FloatingFormItem label="Legenda" name="subtitle">
              <Input />
            </FloatingFormItem>
          </Col>

          <Col xs={24}>
            <FloatingFormItem name="showCountAboveZero" valuePropName="checked">
              <Checkbox>
                Mostrar apenas itens com contagem acima de zero
              </Checkbox>
            </FloatingFormItem>
          </Col>

          <Col xs={24}>
            <FloatingFormItem name="showOnlyChecked" valuePropName="checked">
              <Checkbox>Mostrar apenas itens marcados (checkados)</Checkbox>
            </FloatingFormItem>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default InventoryReportP7Modal;
